import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { EmailScreen } from '../components/Interior/EmailScreen';
import { OtpEntryScreen } from '../components/Interior/OtpEntryScreen';
import { ConsentScreen } from '../components/Interior/ConsentScreen';
import { LoadingScreen } from '../components/Interior/LoadingScreen';
import { PressAnyKeyScreen } from '../components/Interior/PressAnyKeyScreen';
import { PreferenceScreen } from '../components/Interior/PreferenceScreen';
import { ModelSelectionScreen } from '../components/Interior/ModelSelectionScreen';
import { interiorModels } from '../data/interiorMockData';
import { ThemeProvider } from '../components/styles/ThemeContext';
type InteriorScreen = 'models' | 'email' | 'otpEntry' | 'consent' | 'loading' | 'pressKey' | 'preference';
// Map screen names to URL path segments
const screenToPath: Record<InteriorScreen, string> = {
  models: '',
  email: 'email',
  otpEntry: 'verify',
  consent: 'disclaimer',
  loading: 'loading',
  pressKey: 'ready',
  preference: 'preference'
};
// Map URL path segments back to screen names
const pathToScreen: Record<string, InteriorScreen> = {
  '': 'models',
  email: 'email',
  verify: 'otpEntry',
  disclaimer: 'consent',
  loading: 'loading',
  ready: 'pressKey',
  preference: 'preference'
};
function buildUrl(screen: InteriorScreen): string {
  const segment = screenToPath[screen];
  return segment === '' ? '/' : `/${segment}`;
}
function parseUrl(pathname: string): InteriorScreen {
  const segment = pathname.replace(/^\//, '').split('/')[0] || '';
  return pathToScreen[segment] || 'models';
}
function InteriorAppContent() {
  const [currentScreen, setCurrentScreen] = useState<InteriorScreen>(() => parseUrl(window.location.pathname));
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const navigateTo = useCallback((screen: InteriorScreen) => {
    setCurrentScreen(screen);
    window.history.pushState({}, '', buildUrl(screen));
  }, []);
  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      setCurrentScreen(parseUrl(window.location.pathname));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  // ─── Navigation handlers ────────────────────────────────────────────
  const handleModelSelect = (modelId: string) => {
    setSelectedModelId(modelId);
    navigateTo('email');
  };
  const handleEmailSubmit = (email: string) => {
    setUserEmail(email);
    navigateTo('otpEntry');
  };
  const handleOtpSubmit = (_code: string) => {
    navigateTo('consent');
  };
  const handleConsentAgree = () => {
    navigateTo('loading');
  };
  const handleLoadingComplete = () => {
    navigateTo('pressKey');
  };
  const handlePressKey = () => {
    navigateTo('preference');
  };
  // Guest access skips authentication but still requires the consent gate
  const handleSkipAuth = () => {
    navigateTo('consent');
  };
  const handleSelectGuidedTour = () => {
    window.open('https://app-visualizer.resvr.com?mode=guided', '_blank');
  };
  const handleSelectMouseKeyboard = () => {
    window.open('https://app-visualizer.resvr.com?mode=manual', '_blank');
  };
  const handleBack = () => {
    if (currentScreen === 'email') {
      navigateTo('models');
    } else if (currentScreen === 'otpEntry') {
      navigateTo('email');
    } else if (currentScreen === 'consent') {
      navigateTo('otpEntry');
    }
    // models is the start — back button does nothing
  };
  return <div className="relative w-full h-screen overflow-hidden" data-id="element-235">
      <AnimatePresence mode="wait" data-id="element-236">
        {currentScreen === 'models' && <motion.div key="models" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-237">
            <ModelSelectionScreen models={interiorModels} onSelectModel={handleModelSelect} onBack={handleBack} data-id="element-238" />
          </motion.div>}

        {currentScreen === 'email' && <motion.div key="email" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-239">
            <EmailScreen onSubmit={handleEmailSubmit} onBack={handleBack} onSkip={handleSkipAuth} context="One last step — we'll save your selection so you can return to it anytime." data-id="element-240" />
          </motion.div>}

        {currentScreen === 'otpEntry' && <motion.div key="otpEntry" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-241">
            <OtpEntryScreen email={userEmail || ''} onSubmit={handleOtpSubmit} onBack={handleBack} onSkip={handleSkipAuth} data-id="element-242" />
          </motion.div>}

        {currentScreen === 'consent' && <motion.div key="consent" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-243">
            <ConsentScreen onAgree={handleConsentAgree} onBack={handleBack} data-id="element-244" />
          </motion.div>}

        {currentScreen === 'loading' && <motion.div key="loading" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-245">
            <LoadingScreen onComplete={handleLoadingComplete} data-id="element-246" />
          </motion.div>}

        {currentScreen === 'pressKey' && <motion.div key="pressKey" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-247">
            <PressAnyKeyScreen onContinue={handlePressKey} data-id="element-248" />
          </motion.div>}

        {currentScreen === 'preference' && <motion.div key="preference" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-249">
            <PreferenceScreen onSelectGuidedTour={handleSelectGuidedTour} onSelectMouseKeyboard={handleSelectMouseKeyboard} data-id="element-250" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function InteriorApp() {
  return <ThemeProvider data-id="element-251">
      <InteriorAppContent data-id="element-252" />
    </ThemeProvider>;
}